<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import PostSaleStatusPageBody from '../components/PostSaleStatusPageBody.vue';
import PostSaleStatusPageHeader from '../components/PostSaleStatusPageHeader.vue';
import PostSaleStatusPageOverlays from '../components/PostSaleStatusPageOverlays.vue';
import { usePostSaleCaseDetail } from '../composables/usePostSaleCaseDetail';
import { usePostSaleSalesDetailActions } from '../composables/usePostSaleSalesDetailActions';
import { usePostSaleExternalAction } from '../composables/usePostSaleExternalAction';
import { usePostSaleStatusDeadlineActions } from '../composables/usePostSaleStatusDeadlineActions';
import { usePostSaleStatusTransitionFeedback } from '../composables/usePostSaleStatusTransitionFeedback';
import { useSalesTransactionDetail } from '../composables/useSalesTransactionDetail';
import { postSaleStatusOptions } from '../composables/postSaleStatusOptions';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const caseId = computed(() => route.params.postSaleCaseId?.toString() || '');
const selectedStatusEvent = ref(null);

const postSale = usePostSaleCaseDetail({
  t,
  notify: useAlert,
  refresh: () => postSale.load(caseId.value),
});
const transactionId = computed(
  () => postSale.selectedCase.value?.property_transaction_id || ''
);
const transaction = useSalesTransactionDetail({ id: transactionId, t });
const actions = usePostSaleSalesDetailActions({
  postSaleCase: postSale.selectedCase,
  t,
  notify: useAlert,
});
const statusOptions = computed(() => postSaleStatusOptions(t));
const dashboardQuery = computed(() => {
  const { agenda_action: _agendaAction, ...query } = route.query;
  return query;
});

watch(caseId, value => postSale.load(value), { immediate: true });

const pushTo = (name, params = {}, query = dashboardQuery.value) =>
  router.push({
    name,
    params: { accountId: route.params.accountId, ...params },
    query,
  });
const backToPostSale = () => pushTo('operacao_imobiliaria_pos_venda');
const openCorrection = () =>
  pushTo('operacao_imobiliaria_pos_venda_devolucao', {
    salesTransactionId: transactionId.value,
  });
const openNucleoRequest = () =>
  pushTo('operacao_imobiliaria_pos_venda_pedidos_servico', {
    salesTransactionId: transactionId.value,
  });
const externalAction = usePostSaleExternalAction({ t, notify: useAlert });
const deadlineActions = usePostSaleStatusDeadlineActions({
  initialAction: route.query.agenda_action?.toString() || '',
  openNucleo: openNucleoRequest,
  postSaleCase: postSale.selectedCase,
  requestExternal: externalAction.request,
});
const transitionFeedback = usePostSaleStatusTransitionFeedback({
  closeTransition: deadlineActions.close,
  transition: payload => postSale.transition(payload),
});
const openAnalysis = topic =>
  pushTo(
    'operacao_imobiliaria_pos_venda_analise',
    { postSaleCaseId: caseId.value },
    { ...dashboardQuery.value, ...(topic ? { cardId: topic.id } : {}) }
  );

const assignResponsible = async responsibleUserId => {
  const assigned = await actions.assign(responsibleUserId);
  if (assigned) await postSale.load(caseId.value);
};

const returnAfter = action =>
  action().then(result => result && backToPostSale());
const cancelSale = payload => returnAfter(() => actions.cancel(payload));
const completeProcess = () => returnAfter(actions.complete);

const saveNote = payload =>
  postSale.save(postSale.selectedCase.value.id, payload);

const deleteNote = note =>
  postSale.remove(postSale.selectedCase.value.id, note);
const deleteTopic = topic => postSale.removeTopic(caseId.value, topic);
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full bg-lais-bg-soft"
  >
    <PostSaleStatusPageHeader
      v-if="transaction.transaction.value"
      :transaction="transaction.transaction.value"
      @back="backToPostSale"
    />

    <PostSaleStatusPageBody
      :agents="actions.agents.value"
      :assigning="actions.assigning.value"
      :cancelling="actions.cancelling.value"
      :can-manage-notes="postSale.selectedCase.value?.permissions?.can_add_note"
      :can-manage-topics="postSale.selectedCase.value?.permissions?.can_manage"
      :case-data="postSale.selectedCase.value"
      :completing="actions.completing.value"
      :editing-note="postSale.editingNote.value"
      :loading="postSale.loadingDetail.value || transaction.loading.value"
      :loading-agents="actions.loadingAgents.value"
      :loading-notes="postSale.loadingNotes.value"
      :loading-topics="postSale.loadingTopics.value"
      :notes="postSale.notes.value"
      :saving-note="postSale.savingNote.value"
      :show-note-form="postSale.showNoteForm.value"
      :topics="postSale.topics.value"
      :topic-catalog="postSale.topicCatalog.value"
      @assign="assignResponsible"
      @back="backToPostSale"
      @cancel="cancelSale"
      @change-status="deadlineActions.openTransition()"
      @complete="completeProcess"
      @correction="openCorrection"
      @create-note="postSale.openCreate"
      @close-note-form="postSale.closeForm"
      @delete-note="deleteNote"
      @delete-topic="deleteTopic"
      @edit-analysis="openAnalysis"
      @edit-note="postSale.openEdit"
      @edit-topic="openAnalysis"
      @nucleo="openNucleoRequest"
      @deadline-action="deadlineActions.open"
      @open-analysis="openAnalysis()"
      @save-note="saveNote"
      @select-status-event="selectedStatusEvent = $event"
    />
    <PostSaleStatusPageOverlays
      :agenda-action="deadlineActions.agendaAction.value"
      :case-data="postSale.selectedCase.value"
      :saving="transitionFeedback.saving.value"
      :selected-status-event="selectedStatusEvent"
      :show-status-change-feedback="transitionFeedback.showFeedback.value"
      :show-transition="deadlineActions.transitionOpen.value"
      :status-options="statusOptions"
      @close-status-detail="selectedStatusEvent = null"
      @close-status-change-feedback="transitionFeedback.closeFeedback"
      @close-transition="deadlineActions.close"
      @submit-transition="transitionFeedback.submit"
    />
  </LaisGamificationSurface>
</template>
